import { useEffect, useState } from 'react';
import { apiRequest, jsonPost } from '../services/api-client';
import type { RemoteConnection } from '../hooks/use-local-render-destinations';

interface Approval { userCode: string; verificationUrl: string; expiresAt: number; pollAfter: number }
interface Progress { state: 'pending' | 'connected' | 'expired' | 'denied'; pollAfter?: number; message?: string }
const PREFIX = '/api/local/remote';

export function ProductionAccountConnection({ connection }: { connection: RemoteConnection }) {
  const [approval, setApproval] = useState<Approval | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (!approval) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      if (Date.now() >= approval.expiresAt) { setApproval(null); setMessage('Approval expired. Connect again.'); return; }
      try {
        const result = await apiRequest<Progress>(PREFIX + '/connect/poll', jsonPost({}));
        if (!active) return;
        if (result.state === 'pending') timer = setTimeout(() => void poll(), result.pollAfter ?? approval.pollAfter);
        else { setApproval(null); setMessage(result.message ?? (result.state === 'connected' ? 'Account approved. Checking production access and renderer release…' : 'Connect again to approve your account.')); }
      } catch {
        if (active) timer = setTimeout(() => void poll(), Math.max(approval.pollAfter, 10_000));
      }
    };
    timer = setTimeout(() => void poll(), approval.pollAfter);
    return () => { active = false; clearTimeout(timer); };
  }, [approval]);
  const connect = async () => {
    setBusy(true); setMessage('');
    try { setApproval(await apiRequest<Approval>(PREFIX + '/connect', jsonPost({}))); }
    catch { setMessage('Production sign-in could not start. Check the server’s Clerk connection configuration.'); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true); setApproval(null);
    try { await apiRequest(PREFIX + '/disconnect', jsonPost({})); setMessage('Production account disconnected from this computer. Submitted jobs continue on production.'); }
    catch { setMessage('Unable to disconnect. Try again.'); }
    finally { setBusy(false); }
  };
  return <div className="production-account-connection">
    {connection.connected ? connection.connectConfigured && <button type="button" disabled={busy} onClick={() => void disconnect()}>DISCONNECT PRODUCTION ACCOUNT</button>
      : <button type="button" disabled={busy || !connection.connectConfigured || !!approval} onClick={() => void connect()}>CONNECT PRODUCTION ACCOUNT</button>}
    {approval && <div role="status"><p>Approve this computer with your production account.</p>
      <p>APPROVAL CODE: <strong>{approval.userCode}</strong></p>
      <a href={approval.verificationUrl} target="_blank" rel="noopener noreferrer">SIGN IN ON PRODUCTION</a>
      <p>Enter the code, review the requested access, and approve the connection. Waiting for approval…</p>
      <button type="button" onClick={() => { setApproval(null); setMessage('Approval closed. The code will expire automatically.'); }}>CLOSE APPROVAL</button>
    </div>}
    {message && <p role="status">{message}</p>}
  </div>;
}
